import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { 
  SafetyOutlined, 
  LockOutlined, 
  SafetyCertificateOutlined, 
  CheckCircleOutlined, 
  FileTextOutlined, 
  GlobalOutlined,
  DashboardOutlined,
  FormOutlined,
  EditOutlined
} from '@ant-design/icons'
import { useTheme } from '../../contexts/ThemeContext'
import './HomePage.css'
import './HomePage-logo.css'
import './HomePage-responsive.css'

const HomePage = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { theme } = useTheme()
  const [logoLoaded, setLogoLoaded] = useState(false)
  const [activeTab, setActiveTab] = useState('company')
  const [activeFilter, setActiveFilter] = useState('all')

  return (
    <div className="home-container">
      {/* Subtle gradient background */}
      <div className="gradient-background" />

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-card">
            <div className="trust-badge">
              <SafetyOutlined /> {t('home.hero.badge')}
            </div>
            <h1 className="hero-title">
              {t('home.hero.title')}
            </h1>
            <p className="hero-subtitle">
              {t('home.hero.subtitle')}
            </p>
            
            <div className="hero-buttons">
              <button 
                className="primary-button responsive-touch-target"
                onClick={() => navigate('/pricing')}
              >
                <FileTextOutlined className="button-icon" />
                <span>{t('home.hero.pricingButton')}</span>
              </button>
              <button 
                className="secondary-button responsive-touch-target"
                onClick={() => navigate('/lookup')}
              >
                <CheckCircleOutlined className="button-icon" />
                <span>{t('home.hero.checkStatusButton')}</span>
              </button>
            </div>
            
            <div className="security-badges">
              <div className="badge-item">
                <LockOutlined />
                <span>{t('home.hero.encryption')}</span>
              </div>
              <div className="badge-item">
                <SafetyCertificateOutlined />
                <span>{t('home.hero.iso')}</span>
              </div>
              <div className="badge-item">
                <GlobalOutlined />
                <span>{t('home.hero.gdpr')}</span>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* How It Works Section */}
      <section className="how-it-works-section">
        <div className="container">
          <header className="header">
            <div className="logo-wrapper">
              <img 
                src={theme === 'dark' ? "/wp-high-resolution-logo-darkmode.svg" : "/wp-high-resolution-logo-transparent.svg"} 
                alt="WhistlePro" 
                className={`logo-image ${logoLoaded ? 'loaded' : 'loading'}`}
                onLoad={() => setLogoLoaded(true)}
              />
            </div>
            <p className="tagline">{t('home.howItWorks.tagline')}</p>
          </header>

          <div className="tab-nav" id="tabNav">
            <div className="tab-indicator" id="tabIndicator" style={{
              width: activeTab === 'company' ? 'calc(33.33% - 0.333rem)' : 'calc(33.33% - 0.333rem)',
              left: activeTab === 'company' ? '0' : 
                    activeTab === 'employee' ? 'calc(33.33%)' : 
                    'calc(66.66%)',
              top: '0.375rem'
            }} />
            <button 
              className={`tab-btn ${activeTab === 'company' ? 'active' : ''}`}
              onClick={() => setActiveTab('company')}
            >
              <DashboardOutlined className="tab-icon-antd" />
              <span>{t('home.howItWorks.tabs.companies')}</span>
            </button>
            <button 
              className={`tab-btn ${activeTab === 'employee' ? 'active' : ''}`}
              onClick={() => setActiveTab('employee')}
            >
              <SafetyOutlined className="tab-icon-antd" />
              <span>{t('home.howItWorks.tabs.employees')}</span>
            </button>
            <button 
              className={`tab-btn ${activeTab === 'features' ? 'active' : ''}`}
              onClick={() => setActiveTab('features')}
            >
              <CheckCircleOutlined className="tab-icon-antd" />
              <span>{t('home.howItWorks.tabs.features')}</span>
            </button>
          </div>

          {/* Company Journey Tab */}
          <div id="company" className={`tab-content ${activeTab === 'company' ? 'active' : ''}`}>
            <div className="journey">
              {/* Step 1: Sign Up */}
              <div className="step">
                <div className="step-content">
                  <div className="step-header">
                    <div className="step-number">1</div>
                    <h3 className="step-title">{t('home.howItWorks.company.step1.title')}</h3>
                  </div>
                  <p className="step-description">
                    {t('home.howItWorks.company.step1.description')}
                  </p>
                  <div className="timeline">
                    <div className="timeline-item">
                      <div className="timeline-dot"></div>
                      <div className="timeline-title">{t('home.howItWorks.company.step1.timeline.visit.title')}</div>
                      <div className="timeline-description">{t('home.howItWorks.company.step1.timeline.visit.description')}</div>
                    </div>
                    <div className="timeline-item">
                      <div className="timeline-dot"></div>
                      <div className="timeline-title">{t('home.howItWorks.company.step1.timeline.details.title')}</div>
                      <div className="timeline-description">{t('home.howItWorks.company.step1.timeline.details.description')}</div>
                    </div>
                    <div className="timeline-item">
                      <div className="timeline-dot"></div>
                      <div className="timeline-title">{t('home.howItWorks.company.step1.timeline.create.title')}</div>
                      <div className="timeline-description">{t('home.howItWorks.company.step1.timeline.create.description')}</div>
                    </div>
                  </div>
                </div>
                <div className="browser">
                  <div className="browser-header">
                    <div className="browser-dots">
                      <div className="browser-dot"></div>
                      <div className="browser-dot"></div>
                      <div className="browser-dot"></div>
                    </div>
                  </div>
                  <div className="browser-content">
                    <h4 style={{marginBottom: '2rem'}}>{t('home.howItWorks.company.step1.form.title')}</h4>
                    <form>
                      <div className="form-group">
                        <label className="form-label">{t('home.howItWorks.company.step1.form.companyName')}</label>
                        <input type="text" className="form-input" value={t('home.examples.companyName', 'TechCorp EU GmbH')} readOnly />
                      </div>
                      <div className="form-group">
                        <label className="form-label">{t('home.howItWorks.company.step1.form.employees')}</label>
                        <select className="form-select">
                          <option>{t('home.howItWorks.company.step1.form.employeeOptions.small')}</option>
                          <option selected>{t('home.howItWorks.company.step1.form.employeeOptions.medium')}</option>
                          <option>{t('home.howItWorks.company.step1.form.employeeOptions.large')}</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label className="form-label">{t('home.howItWorks.company.step1.form.industry')}</label>
                        <select className="form-select">
                          <option>{t('home.howItWorks.company.step1.form.industries.healthcare')}</option>
                          <option>{t('home.howItWorks.company.step1.form.industries.financial')}</option>
                          <option selected>{t('home.howItWorks.company.step1.form.industries.technology')}</option>
                          <option>{t('home.howItWorks.company.step1.form.industries.manufacturing')}</option>
                        </select>
                      </div>
                      <button type="button" className="btn btn-primary">
                        {t('home.howItWorks.company.step1.form.createButton')}
                        <span className="btn-icon">→</span>
                      </button>
                    </form>
                  </div>
                </div>
              </div>

              {/* Step 2: Setup Portal */}
              <div className="step">
                <div className="step-content">
                  <div className="step-header">
                    <div className="step-number">2</div>
                    <h3 className="step-title">{t('home.howItWorks.company.step2.title')}</h3>
                  </div>
                  <p className="step-description">
                    {t('home.howItWorks.company.step2.description')}
                  </p>
                  <div className="features-grid">
                    <div className="feature-card">
                      <div className="feature-icon">
                        <EditOutlined style={{ color: 'white' }} />
                      </div>
                      <div className="feature-content">
                        <div className="feature-title">{t('home.howItWorks.company.step2.features.branding.title')}</div>
                        <div className="feature-description">{t('home.howItWorks.company.step2.features.branding.description')}</div>
                      </div>
                    </div>
                    <div className="feature-card">
                      <div className="feature-icon">
                        <FormOutlined style={{ color: 'white' }} />
                      </div>
                      <div className="feature-content">
                        <div className="feature-title">{t('home.howItWorks.company.step2.features.categories.title')}</div>
                        <div className="feature-description">{t('home.howItWorks.company.step2.features.categories.description')}</div>
                      </div>
                    </div>
                    <div className="feature-card">
                      <div className="feature-icon">
                        <GlobalOutlined style={{ color: 'white' }} />
                      </div>
                      <div className="feature-content">
                        <div className="feature-title">{t('home.howItWorks.company.step2.features.languages.title')}</div>
                        <div className="feature-description">{t('home.howItWorks.company.step2.features.languages.description')}</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="browser">
                  <div className="browser-header">
                    <div className="browser-dots">
                      <div className="browser-dot"></div>
                      <div className="browser-dot"></div>
                      <div className="browser-dot"></div>
                    </div>
                  </div>
                  <div className="browser-content">
                    <h4 style={{marginBottom: '2rem'}}>{t('home.howItWorks.company.step2.form.title')}</h4>
                    <div className="form-group">
                      <label className="form-label">{t('home.howItWorks.company.step2.form.categories')}</label>
                      <div style={{display: 'grid', gap: '0.75rem'}}>
                        <label className="card" style={{display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '1rem'}}>
                          <input type="checkbox" defaultChecked style={{width: '20px', height: '20px'}} />
                          <span>{t('home.howItWorks.company.step2.form.categoryOptions.dataPrivacy')}</span>
                        </label>
                        <label className="card" style={{display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '1rem'}}>
                          <input type="checkbox" defaultChecked style={{width: '20px', height: '20px'}} />
                          <span>{t('home.howItWorks.company.step2.form.categoryOptions.security')}</span>
                        </label>
                        <label className="card" style={{display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '1rem'}}>
                          <input type="checkbox" defaultChecked style={{width: '20px', height: '20px'}} />
                          <span>{t('home.howItWorks.company.step2.form.categoryOptions.financial')}</span>
                        </label>
                        <label className="card" style={{display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '1rem'}}>
                          <input type="checkbox" defaultChecked style={{width: '20px', height: '20px'}} />
                          <span>{t('home.howItWorks.company.step2.form.categoryOptions.harassment')}</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 3: Get Link */}
              <div className="step">
                <div className="step-content">
                  <div className="step-header">
                    <div className="step-number">3</div>
                    <h3 className="step-title">{t('home.howItWorks.company.step3.title')}</h3>
                  </div>
                  <p className="step-description">
                    {t('home.howItWorks.company.step3.description')}
                  </p>
                  <div className="link-display">
                    <span className="link-url">https://report.whistlepro.eu/techcorp-eu-af3d92</span>
                    <button className="copy-btn">{t('home.howItWorks.company.step3.copyButton')}</button>
                  </div>
                  <p style={{color: 'var(--gray)', fontSize: '0.875rem', marginTop: '1rem'}}>
                    💡 {t('home.howItWorks.company.step3.hint')}
                  </p>
                </div>
                <div className="browser">
                  <div className="browser-header">
                    <div className="browser-dots">
                      <div className="browser-dot"></div>
                      <div className="browser-dot"></div>
                      <div className="browser-dot"></div>
                    </div>
                  </div>
                  <div className="browser-content">
                    <h4 style={{marginBottom: '2rem'}}>{t('home.howItWorks.company.step3.share.title')}</h4>
                    <div className="success-message">
                      <div className="success-icon">✅</div>
                      <p style={{fontWeight: '600', marginBottom: '0.5rem'}}>{t('home.howItWorks.company.step3.share.success')}</p>
                      <p style={{color: 'var(--gray)', fontSize: '0.875rem'}}>{t('home.howItWorks.company.step3.share.successDesc')}</p>
                    </div>
                    <p style={{marginBottom: '1.5rem', color: 'var(--gray-light)'}}>{t('home.howItWorks.company.step3.share.shareVia')}</p>
                    <div style={{display: 'grid', gap: '1rem'}}>
                      <button className="btn btn-primary">
                        📧 {t('home.howItWorks.company.step3.share.emailButton')}
                        <span className="btn-icon">→</span>
                      </button>
                      <button className="btn btn-secondary">
                        📱 {t('home.howItWorks.company.step3.share.qrButton')}
                        <span className="btn-icon">→</span>
                      </button>
                      <button className="btn btn-secondary">
                        📄 {t('home.howItWorks.company.step3.share.posterButton')}
                        <span className="btn-icon">→</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 4: Manage Reports */}
              <div className="step">
                <div className="step-content">
                  <div className="step-header">
                    <div className="step-number">4</div>
                    <h3 className="step-title">{t('home.howItWorks.company.step4.title')}</h3>
                  </div>
                  <p className="step-description">
                    {t('home.howItWorks.company.step4.description')}
                  </p>
                  <div className="timeline">
                    <div className="timeline-item">
                      <div className="timeline-dot"></div>
                      <div className="timeline-title">{t('home.howItWorks.company.step4.timeline.notifications.title')}</div>
                      <div className="timeline-description">{t('home.howItWorks.company.step4.timeline.notifications.description')}</div>
                    </div>
                    <div className="timeline-item">
                      <div className="timeline-dot"></div>
                      <div className="timeline-title">{t('home.howItWorks.company.step4.timeline.review.title')}</div>
                      <div className="timeline-description">{t('home.howItWorks.company.step4.timeline.review.description')}</div>
                    </div>
                    <div className="timeline-item">
                      <div className="timeline-dot"></div>
                      <div className="timeline-title">{t('home.howItWorks.company.step4.timeline.chat.title')}</div>
                      <div className="timeline-description">{t('home.howItWorks.company.step4.timeline.chat.description')}</div>
                    </div>
                    <div className="timeline-item">
                      <div className="timeline-dot"></div>
                      <div className="timeline-title">{t('home.howItWorks.company.step4.timeline.deadlines.title')}</div>
                      <div className="timeline-description">{t('home.howItWorks.company.step4.timeline.deadlines.description')}</div>
                    </div>
                  </div>
                </div>
                <div className="browser">
                  <div className="browser-header">
                    <div className="browser-dots">
                      <div className="browser-dot"></div>
                      <div className="browser-dot"></div>
                      <div className="browser-dot"></div>
                    </div>
                  </div>
                  <div className="browser-content">
                    <h4 style={{marginBottom: '2rem'}}>{t('home.howItWorks.company.step4.dashboard.title')}</h4>
                    <div className="card dashboard-card">
                      <div className="report-info">
                        <div className="report-id">#WP-2025-0847</div>
                        <div className="report-meta">{t('home.howItWorks.company.step2.form.categoryOptions.financial')} • {t('home.howItWorks.company.step4.dashboard.timeAgo.hours', { count: 2 })}</div>
                      </div>
                      <span className="status-badge status-new">{t('home.howItWorks.company.step4.dashboard.statuses.new')}</span>
                    </div>
                    <div className="card dashboard-card">
                      <div className="report-info">
                        <div className="report-id">#WP-2025-0846</div>
                        <div className="report-meta">{t('home.howItWorks.company.step2.form.categoryOptions.dataPrivacy')} • {t('home.howItWorks.company.step4.dashboard.timeAgo.yesterday')}</div>
                      </div>
                      <span className="status-badge status-progress">{t('home.howItWorks.company.step4.dashboard.statuses.progress')}</span>
                    </div>
                    <div className="card dashboard-card">
                      <div className="report-info">
                        <div className="report-id">#WP-2025-0845</div>
                        <div className="report-meta">{t('categories.workplaceSafety', 'Workplace Safety')} • {t('home.howItWorks.company.step4.dashboard.timeAgo.days', { count: 3 })}</div>
                      </div>
                      <span className="status-badge status-resolved">{t('home.howItWorks.company.step4.dashboard.statuses.resolved')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Employee Journey Tab */}
          <div id="employee" className={`tab-content ${activeTab === 'employee' ? 'active' : ''}`}>
            <div className="journey">
              {/* Step 1: Access Link */}
              <div className="step">
                <div className="step-content">
                  <div className="step-header">
                    <div className="step-number">1</div>
                    <h3 className="step-title">{t('home.howItWorks.employee.step1.title')}</h3>
                  </div>
                  <p className="step-description">
                    {t('home.howItWorks.employee.step1.description')}
                  </p>
                  <div style={{background: 'var(--card-bg)', padding: '1.5rem', borderRadius: '8px', border: '1px solid var(--border)'}}>
                    <p style={{fontWeight: '600', marginBottom: '0.5rem', color: 'var(--gray-light)'}}>📧 {t('home.howItWorks.employee.step1.email.title')}</p>
                    <p style={{color: 'var(--gray)', fontSize: '0.875rem', lineHeight: 1.6}}>
                      {t('home.howItWorks.employee.step1.email.content')}
                    </p>
                    <div className="link-display" style={{marginTop: '1rem'}}>
                      <span className="link-url">https://report.whistlepro.eu/techcorp-eu-af3d92</span>
                    </div>
                  </div>
                </div>
                <div className="browser">
                  <div className="browser-header">
                    <div className="browser-dots">
                      <div className="browser-dot"></div>
                      <div className="browser-dot"></div>
                      <div className="browser-dot"></div>
                    </div>
                  </div>
                  <div className="browser-content">
                    <div style={{textAlign: 'center', padding: '2rem 0'}}>
                      <h3 style={{marginBottom: '1rem'}}>{t('home.howItWorks.employee.step1.portal.company')}</h3>
                      <h4 style={{marginBottom: '2rem', color: 'var(--gray-light)', fontWeight: '400'}}>{t('home.howItWorks.employee.step1.portal.title')}</h4>
                    </div>
                    <div style={{background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.05), rgba(99, 102, 241, 0.05))', padding: '2rem', borderRadius: '8px', marginBottom: '2rem', border: '1px solid rgba(37, 99, 235, 0.2)'}}>
                      <p style={{fontWeight: '600', marginBottom: '1rem'}}>🔒 {t('home.howItWorks.employee.step1.portal.privacy.title')}</p>
                      <ul style={{color: 'var(--gray-light)', fontSize: '0.875rem', listStyle: 'none', display: 'grid', gap: '0.5rem'}}>
                        <li>✓ {t('home.howItWorks.employee.step1.portal.privacy.points.noInfo')}</li>
                        <li>✓ {t('home.howItWorks.employee.step1.portal.privacy.points.noTracking')}</li>
                        <li>✓ {t('home.howItWorks.employee.step1.portal.privacy.points.encryption')}</li>
                        <li>✓ {t('home.howItWorks.employee.step1.portal.privacy.points.anonymous')}</li>
                      </ul>
                    </div>
                    <button className="btn btn-primary" style={{width: '100%'}}>
                      {t('home.howItWorks.employee.step1.portal.submitButton')}
                      <span className="btn-icon">→</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Step 2: Submit Report */}
              <div className="step">
                <div className="step-content">
                  <div className="step-header">
                    <div className="step-number">2</div>
                    <h3 className="step-title">{t('home.howItWorks.employee.step2.title')}</h3>
                  </div>
                  <p className="step-description">
                    {t('home.howItWorks.employee.step2.description')}
                  </p>
                  <div className="features-grid">
                    <div className="feature-card">
                      <div className="feature-icon">🚫</div>
                      <div className="feature-title">{t('home.howItWorks.employee.step2.features.noLogin.title')}</div>
                      <div className="feature-description">{t('home.howItWorks.employee.step2.features.noLogin.description')}</div>
                    </div>
                    <div className="feature-card">
                      <div className="feature-icon">📎</div>
                      <div className="feature-title">{t('home.howItWorks.employee.step2.features.evidence.title')}</div>
                      <div className="feature-description">{t('home.howItWorks.employee.step2.features.evidence.description')}</div>
                    </div>
                    <div className="feature-card">
                      <div className="feature-icon">🔐</div>
                      <div className="feature-title">{t('home.howItWorks.employee.step2.features.encrypted.title')}</div>
                      <div className="feature-description">{t('home.howItWorks.employee.step2.features.encrypted.description')}</div>
                    </div>
                  </div>
                </div>
                <div className="browser">
                  <div className="browser-header">
                    <div className="browser-dots">
                      <div className="browser-dot"></div>
                      <div className="browser-dot"></div>
                      <div className="browser-dot"></div>
                    </div>
                  </div>
                  <div className="browser-content">
                    <h4 style={{marginBottom: '2rem'}}>{t('home.howItWorks.employee.step2.form.title')}</h4>
                    <form>
                      <div className="form-group">
                        <label className="form-label">{t('home.howItWorks.employee.step2.form.category')}</label>
                        <select className="form-select">
                          <option>{t('home.howItWorks.employee.step2.form.categoryPlaceholder')}</option>
                          <option>{t('home.howItWorks.company.step2.form.categoryOptions.dataPrivacy')}</option>
                          <option>{t('home.howItWorks.company.step2.form.categoryOptions.security')}</option>
                          <option selected>{t('home.howItWorks.company.step2.form.categoryOptions.financial')}</option>
                          <option>{t('home.howItWorks.company.step2.form.categoryOptions.harassment')}</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label className="form-label">{t('home.howItWorks.employee.step2.form.description')}</label>
                        <textarea className="form-textarea">{t('home.howItWorks.employee.step2.form.exampleText', 'I\'ve noticed suspicious transactions in our Q3 reports. Multiple invoices appear to be duplicated with different amounts...')}</textarea>
                      </div>
                      <div className="form-group">
                        <label className="form-label">{t('home.howItWorks.employee.step2.form.attachments')}</label>
                        <div className="card" style={{textAlign: 'center', padding: '2rem', borderStyle: 'dashed'}}>
                          <p style={{color: 'var(--gray)'}}>{t('home.howItWorks.employee.step2.form.uploadHint')}</p>
                        </div>
                      </div>
                      <button type="button" className="btn btn-primary">
                        {t('home.howItWorks.employee.step2.form.submitButton')}
                        <span className="btn-icon">→</span>
                      </button>
                    </form>
                  </div>
                </div>
              </div>

              {/* Step 3: Get Case ID */}
              <div className="step">
                <div className="step-content">
                  <div className="step-header">
                    <div className="step-number">3</div>
                    <h3 className="step-title">{t('home.howItWorks.employee.step3.title')}</h3>
                  </div>
                  <p className="step-description">
                    {t('home.howItWorks.employee.step3.description')}
                  </p>
                  <div className="case-id-display">
                    <div className="case-id-label">{t('home.howItWorks.employee.step3.caseIdLabel')}</div>
                    <div className="case-id">WP-2025-0847-3F9A</div>
                    <button className="copy-btn">{t('home.howItWorks.employee.step3.copyButton')}</button>
                  </div>
                  <p style={{color: 'var(--gray)', fontSize: '0.875rem', textAlign: 'center'}}>
                    {t('home.howItWorks.employee.step3.hint')}
                  </p>
                </div>
                <div className="browser">
                  <div className="browser-header">
                    <div className="browser-dots">
                      <div className="browser-dot"></div>
                      <div className="browser-dot"></div>
                      <div className="browser-dot"></div>
                    </div>
                  </div>
                  <div className="browser-content">
                    <div style={{textAlign: 'center', padding: '3rem 0'}}>
                      <div className="success-icon">✅</div>
                      <h4 style={{marginBottom: '1rem'}}>{t('home.howItWorks.employee.step3.success.title')}</h4>
                      <p style={{color: 'var(--gray-light)', marginBottom: '2rem'}}>
                        {t('home.howItWorks.employee.step3.success.description')}
                      </p>
                      <div className="case-id-display">
                        <div className="case-id-label">{t('home.howItWorks.employee.step3.caseIdLabel')}</div>
                        <div className="case-id">WP-2025-0847-3F9A</div>
                        <button className="copy-btn">{t('home.howItWorks.employee.step3.copyButton')}</button>
                      </div>
                      <div className="warning-box">
                        <span className="warning-icon">⚠️</span>
                        <div className="warning-content">
                          <div className="warning-title">{t('home.howItWorks.employee.step3.success.warning.title')}</div>
                          <div className="warning-text">
                            {t('home.howItWorks.employee.step3.success.warning.description')}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 4: Check Responses */}
              <div className="step">
                <div className="step-content">
                  <div className="step-header">
                    <div className="step-number">4</div>
                    <h3 className="step-title">{t('home.howItWorks.employee.step4.title')}</h3>
                  </div>
                  <p className="step-description">
                    {t('home.howItWorks.employee.step4.description')}
                  </p>
                  <div className="timeline">
                    <div className="timeline-item">
                      <div className="timeline-dot"></div>
                      <div className="timeline-title">{t('home.howItWorks.employee.step4.timeline.return.title')}</div>
                      <div className="timeline-description">{t('home.howItWorks.employee.step4.timeline.return.description')}</div>
                    </div>
                    <div className="timeline-item">
                      <div className="timeline-dot"></div>
                      <div className="timeline-title">{t('home.howItWorks.employee.step4.timeline.enter.title')}</div>
                      <div className="timeline-description">{t('home.howItWorks.employee.step4.timeline.enter.description')}</div>
                    </div>
                    <div className="timeline-item">
                      <div className="timeline-dot"></div>
                      <div className="timeline-title">{t('home.howItWorks.employee.step4.timeline.view.title')}</div>
                      <div className="timeline-description">{t('home.howItWorks.employee.step4.timeline.view.description')}</div>
                    </div>
                  </div>
                </div>
                <div className="browser">
                  <div className="browser-header">
                    <div className="browser-dots">
                      <div className="browser-dot"></div>
                      <div className="browser-dot"></div>
                      <div className="browser-dot"></div>
                    </div>
                  </div>
                  <div className="browser-content">
                    <h4 style={{marginBottom: '2rem'}}>{t('home.howItWorks.employee.step4.form.title')}</h4>
                    <div className="form-group">
                      <label className="form-label">{t('home.howItWorks.employee.step4.form.caseIdLabel')}</label>
                      <input type="text" className="form-input" value="WP-2025-0847-3F9A" style={{fontFamily: "'SF Mono', monospace"}} />
                    </div>
                    <button className="btn btn-primary" style={{marginBottom: '2rem'}}>
                      {t('home.howItWorks.employee.step4.form.checkButton')}
                      <span className="btn-icon">→</span>
                    </button>
                    
                    <div style={{borderTop: '1px solid var(--border)', paddingTop: '2rem'}}>
                      <h5 style={{marginBottom: '1.5rem'}}>{t('home.howItWorks.employee.step4.form.messages')} <span style={{color: 'var(--warning)', fontSize: '0.875rem'}}>{t('home.howItWorks.employee.step4.form.newBadge', { count: 1 })}</span></h5>
                      <div className="card" style={{marginBottom: '1rem'}}>
                        <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem'}}>
                          <span style={{fontWeight: '600'}}>{t('home.howItWorks.employee.step4.form.from')}</span>
                          <span style={{color: 'var(--gray)', fontSize: '0.875rem'}}>{t('home.howItWorks.employee.step4.form.timeAgo', { time: '2 hours' })}</span>
                        </div>
                        <p style={{color: 'var(--gray-light)', fontSize: '0.9375rem'}}>
                          {t('home.howItWorks.employee.step4.form.adminResponse', 
                            'Thank you for your report. We take this matter seriously and have initiated an investigation. Could you provide more details about which invoices appeared duplicated?')}
                        </p>
                      </div>
                      <textarea className="form-textarea" placeholder={t('home.howItWorks.employee.step4.form.replyPlaceholder')}></textarea>
                      <button className="btn btn-primary" style={{marginTop: '1rem'}}>
                        {t('home.howItWorks.employee.step4.form.sendButton')}
                        <span className="btn-icon">→</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Key Features Tab */}
          <div id="features" className={`tab-content ${activeTab === 'features' ? 'active' : ''}`}>
            <div className="features-matrix-section">
              <h2 className="features-matrix-title">
                {t('home.howItWorks.features.title')}
              </h2>
              
              {/* Feature Matrix Container */}
              <div className="feature-matrix-container">
                {/* Background decoration */}
                <div className="matrix-background-decoration"></div>
                
                {/* All features in unified grid */}
                <div className="feature-matrix-grid"
                  style={{
                    '--filter-display-employee': activeFilter === 'all' || activeFilter === 'employee' ? 'flex' : 'none',
                    '--filter-display-company': activeFilter === 'all' || activeFilter === 'company' ? 'flex' : 'none',
                    '--filter-display-security': activeFilter === 'all' || activeFilter === 'security' ? 'flex' : 'none',
                  } as React.CSSProperties}>
                  {/* Employee Features */}
                  <div className="matrix-feature-card" data-category="employee">
                    <div className="category-badge employee-badge">{t('home.howItWorks.features.filters.employee')}</div>
                    <div className="matrix-icon-wrapper">
                      <span className="matrix-icon">🚫</span>
                    </div>
                    <h4>{t('home.howItWorks.features.employee.noAccount.title')}</h4>
                    <p>{t('home.howItWorks.features.employee.noAccount.description')}</p>
                  </div>
                  
                  <div className="matrix-feature-card" data-category="employee">
                    <div className="category-badge employee-badge">{t('home.howItWorks.features.filters.employee')}</div>
                    <div className="matrix-icon-wrapper">
                      <span className="matrix-icon">🔐</span>
                    </div>
                    <h4>{t('home.howItWorks.features.employee.privacy.title')}</h4>
                    <p>{t('home.howItWorks.features.employee.privacy.description')}</p>
                  </div>
                  
                  <div className="matrix-feature-card" data-category="employee">
                    <div className="category-badge employee-badge">{t('home.howItWorks.features.filters.employee')}</div>
                    <div className="matrix-icon-wrapper">
                      <span className="matrix-icon">💬</span>
                    </div>
                    <h4>{t('home.howItWorks.features.employee.chat.title')}</h4>
                    <p>{t('home.howItWorks.features.employee.chat.description')}</p>
                  </div>
                  
                  <div className="matrix-feature-card" data-category="employee">
                    <div className="category-badge employee-badge">{t('home.howItWorks.features.filters.employee')}</div>
                    <div className="matrix-icon-wrapper">
                      <span className="matrix-icon">📱</span>
                    </div>
                    <h4>{t('home.howItWorks.features.employee.anyDevice.title')}</h4>
                    <p>{t('home.howItWorks.features.employee.anyDevice.description')}</p>
                  </div>
                  
                  {/* Company Features */}
                  <div className="matrix-feature-card" data-category="company">
                    <div className="category-badge company-badge">{t('home.howItWorks.features.filters.company')}</div>
                    <div className="matrix-icon-wrapper">
                      <span className="matrix-icon">⚡</span>
                    </div>
                    <h4>{t('home.howItWorks.features.company.compliance.title')}</h4>
                    <p>{t('home.howItWorks.features.company.compliance.description')}</p>
                  </div>
                  
                  <div className="matrix-feature-card" data-category="company">
                    <div className="category-badge company-badge">{t('home.howItWorks.features.filters.company')}</div>
                    <div className="matrix-icon-wrapper">
                      <span className="matrix-icon">📊</span>
                    </div>
                    <h4>{t('home.howItWorks.features.company.dashboard.title')}</h4>
                    <p>{t('home.howItWorks.features.company.dashboard.description')}</p>
                  </div>
                  
                  <div className="matrix-feature-card" data-category="company">
                    <div className="category-badge company-badge">{t('home.howItWorks.features.filters.company')}</div>
                    <div className="matrix-icon-wrapper">
                      <span className="matrix-icon">⏰</span>
                    </div>
                    <h4>{t('home.howItWorks.features.company.deadline.title')}</h4>
                    <p>{t('home.howItWorks.features.company.deadline.description')}</p>
                  </div>
                  
                  <div className="matrix-feature-card" data-category="company">
                    <div className="category-badge company-badge">{t('home.howItWorks.features.filters.company')}</div>
                    <div className="matrix-icon-wrapper">
                      <span className="matrix-icon">📋</span>
                    </div>
                    <h4>{t('home.howItWorks.features.company.audit.title')}</h4>
                    <p>{t('home.howItWorks.features.company.audit.description')}</p>
                  </div>
                  
                  {/* Security Features */}
                  <div className="matrix-feature-card large-card" data-category="security">
                    <div className="category-badge security-badge">{t('home.howItWorks.features.filters.security')}</div>
                    <div className="matrix-icon-wrapper">
                      <SafetyOutlined className="matrix-antd-icon" />
                    </div>
                    <h4>{t('home.howItWorks.features.security.anonymity.title')}</h4>
                    <p>{t('home.howItWorks.features.security.anonymity.description')}</p>
                  </div>
                  
                  <div className="matrix-feature-card large-card" data-category="security">
                    <div className="category-badge security-badge">{t('home.howItWorks.features.filters.security')}</div>
                    <div className="matrix-icon-wrapper">
                      <LockOutlined className="matrix-antd-icon" />
                    </div>
                    <h4>{t('home.howItWorks.features.security.encryption.title')}</h4>
                    <p>{t('home.howItWorks.features.security.encryption.description')}</p>
                  </div>
                  
                  <div className="matrix-feature-card large-card" data-category="security">
                    <div className="category-badge security-badge">{t('home.howItWorks.features.filters.security')}</div>
                    <div className="matrix-icon-wrapper">
                      <SafetyCertificateOutlined className="matrix-antd-icon" />
                    </div>
                    <h4>{t('home.howItWorks.features.security.compliance.title')}</h4>
                    <p>{t('home.howItWorks.features.security.compliance.description')}</p>
                  </div>
                  
                  <div className="matrix-feature-card large-card" data-category="security">
                    <div className="category-badge security-badge">{t('home.howItWorks.features.filters.security')}</div>
                    <div className="matrix-icon-wrapper">
                      <FileTextOutlined className="matrix-antd-icon" />
                    </div>
                    <h4>{t('home.howItWorks.features.security.messaging.title')}</h4>
                    <p>{t('home.howItWorks.features.security.messaging.description')}</p>
                  </div>
                </div>
                
                {/* Interactive filter pills */}
                <div className="feature-filter-pills">
                  <button 
                    className={`filter-pill ${activeFilter === 'all' ? 'active' : ''}`}
                    onClick={() => setActiveFilter('all')}
                  >
                    {t('home.howItWorks.features.filters.all')}
                  </button>
                  <button 
                    className={`filter-pill ${activeFilter === 'employee' ? 'active' : ''}`}
                    onClick={() => setActiveFilter('employee')}
                  >
                    {t('home.howItWorks.features.filters.employee')}
                  </button>
                  <button 
                    className={`filter-pill ${activeFilter === 'company' ? 'active' : ''}`}
                    onClick={() => setActiveFilter('company')}
                  >
                    {t('home.howItWorks.features.filters.company')}
                  </button>
                  <button 
                    className={`filter-pill ${activeFilter === 'security' ? 'active' : ''}`}
                    onClick={() => setActiveFilter('security')}
                  >
                    {t('home.howItWorks.features.filters.security')}
                  </button>
                </div>
              </div>

              {/* Why WhistlePro Section - Redesigned */}
              <div className="why-whistlepro-section">
                <div className="why-header">
                  <span className="why-sparkle">✨</span>
                  <h3>{t('home.howItWorks.features.why.title', 'Why Choose WhistlePro?')}</h3>
                  <p className="why-subtitle">{t('home.howItWorks.features.why.subtitle', 'The smart choice for EU compliance')}</p>
                </div>
                
                <div className="why-features-grid">
                  <div className="why-feature-card">
                    <div className="why-icon-wrapper">
                      <div className="why-icon-bg"></div>
                      <span className="why-icon">⚡</span>
                    </div>
                    <h4>{t('home.howItWorks.features.why.fast.title')}</h4>
                    <p>{t('home.howItWorks.features.why.fast.description')}</p>
                    <div className="why-highlight">{t('home.howItWorks.features.why.fast.highlight')}</div>
                  </div>
                  
                  <div className="why-feature-card">
                    <div className="why-icon-wrapper">
                      <div className="why-icon-bg"></div>
                      <span className="why-icon">🎯</span>
                    </div>
                    <h4>{t('home.howItWorks.features.why.templates.title')}</h4>
                    <p>{t('home.howItWorks.features.why.templates.description')}</p>
                    <div className="why-highlight">{t('home.howItWorks.features.why.templates.highlight')}</div>
                  </div>
                  
                  <div className="why-feature-card">
                    <div className="why-icon-wrapper">
                      <div className="why-icon-bg"></div>
                      <span className="why-icon">💰</span>
                    </div>
                    <h4>{t('home.howItWorks.features.why.pricing.title')}</h4>
                    <p>{t('home.howItWorks.features.why.pricing.description')}</p>
                    <div className="why-highlight">{t('home.howItWorks.features.why.pricing.highlight')}</div>
                  </div>
                </div>
                
                <div className="why-comparison">
                  <div className="comparison-item">
                    <span className="comparison-icon">🚫</span>
                    <div>
                      <strong>{t('home.howItWorks.features.comparison.without.title', 'Without WhistlePro:')}</strong>
                      <span>{t('home.howItWorks.features.comparison.without.description', 'Risk €1M+ fines, complex setup, expensive lawyers')}</span>
                    </div>
                  </div>
                  <div className="comparison-divider"></div>
                  <div className="comparison-item">
                    <span className="comparison-icon">✅</span>
                    <div>
                      <strong>{t('home.howItWorks.features.comparison.with.title', 'With WhistlePro:')}</strong>
                      <span>{t('home.howItWorks.features.comparison.with.description', 'Full compliance, instant setup, fixed low cost')}</span>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Compliance Section */}
      <section className="compliance-section">
        <div className="compliance-container">
          <h2 className="section-title">{t('home.compliance.title')}</h2>
          <div className="compliance-content">
            <div className="compliance-info">
              <h3>{t('home.compliance.heading')}</h3>
              <p>
                {t('home.compliance.description')}
              </p>
              <ul className="compliance-list">
                <li><CheckCircleOutlined /> {t('home.compliance.requirements.channels')}</li>
                <li><CheckCircleOutlined /> {t('home.compliance.requirements.protection')}</li>
                <li><CheckCircleOutlined /> {t('home.compliance.requirements.deadline')}</li>
                <li><CheckCircleOutlined /> {t('home.compliance.requirements.audit')}</li>
                <li><CheckCircleOutlined /> {t('home.compliance.requirements.retention')}</li>
              </ul>
            </div>
            <div className="compliance-badges">
              <div className="compliance-badge">
                <div className="compliance-badge-icon">
                  <SafetyCertificateOutlined />
                </div>
                <span>{t('home.compliance.badges.iso')}</span>
              </div>
              <div className="compliance-badge">
                <div className="compliance-badge-icon">
                  <LockOutlined />
                </div>
                <span>{t('home.compliance.badges.gdpr')}</span>
              </div>
              <div className="compliance-badge">
                <div className="compliance-badge-icon">
                  <CheckCircleOutlined />
                </div>
                <span>{t('home.compliance.badges.soc')}</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default HomePage